import type { SearchQuery } from '../api/chat-api.js';
import { snowflakeAt } from '../contracts/snowflake.js';
import { selectDisplayName } from '../stores/selectors.js';
import type { ChatState } from '../stores/state.js';

/**
 * Busca como a do Discord, em português: o texto livre mais os filtros
 * `de:`, `em:`, `tem:`, `menciona:`, `antes:`, `depois:`, `durante:` e
 * `fixada:`. O leitor é o mesmo no navegador e no celular.
 */
export type SearchFilterKey =
  'de' | 'em' | 'tem' | 'menciona' | 'antes' | 'depois' | 'durante' | 'fixada';

export type SearchFilter = { key: SearchFilterKey; value: string };

export type ParsedSearch = { content: string; filters: SearchFilter[] };

export const SEARCH_FILTERS: Array<{
  key: SearchFilterKey;
  example: string;
  description: string;
}> = [
  { key: 'de', example: 'de: usuário', description: 'Mensagens de uma pessoa' },
  { key: 'em', example: 'em: canal', description: 'Mensagens num canal' },
  {
    key: 'tem',
    example: 'tem: imagem, arquivo, link, vídeo…',
    description: 'Com anexo, link ou enquete',
  },
  {
    key: 'menciona',
    example: 'menciona: usuário',
    description: 'Que citam uma pessoa',
  },
  {
    key: 'antes',
    example: 'antes: 30/09/2026',
    description: 'Antes de uma data',
  },
  {
    key: 'depois',
    example: 'depois: 01/09/2026',
    description: 'Depois de uma data',
  },
  { key: 'durante', example: 'durante: 15/09/2026', description: 'Num dia' },
  { key: 'fixada', example: 'fixada: sim', description: 'Só as fixadas' },
];

/** `tem:` em português → valor do Discord. */
export const SEARCH_HAS_VALUES: Record<string, string> = {
  imagem: 'image',
  imagens: 'image',
  arquivo: 'file',
  arquivos: 'file',
  link: 'link',
  links: 'link',
  video: 'video',
  vídeo: 'video',
  som: 'sound',
  audio: 'sound',
  áudio: 'sound',
  figurinha: 'sticker',
  enquete: 'poll',
  embed: 'embed',
  encaminhada: 'snapshot',
};

const KEYS = new Set<string>(SEARCH_FILTERS.map((filter) => filter.key));
const TOKEN = /(^|\s)([a-zç]+):\s*("[^"]*"|\S*)/giu;

/** Separa o texto livre dos filtros (filtro sem valor fica no texto). */
export function parseSearch(text: string): ParsedSearch {
  const filters: SearchFilter[] = [];
  const content = text
    .replace(TOKEN, (whole, before: string, key: string, value: string) => {
      const name = key.toLowerCase();
      const clean = value.replace(/^"|"$/g, '').trim();
      if (!KEYS.has(name) || !clean) return whole;
      filters.push({ key: name as SearchFilterKey, value: clean });
      return before;
    })
    .replace(/\s+/g, ' ')
    .trim();
  return { content, filters };
}

/** "30/09/2026", "30/09" (ano corrente), "2026-09-30" → meia-noite local. */
export function parseSearchDate(
  value: string,
  now: Date = new Date(),
): Date | null {
  const br = /^(\d{1,2})\/(\d{1,2})(?:\/(\d{2,4}))?$/.exec(value);
  if (br) {
    const year = br[3]
      ? Number(br[3].length === 2 ? `20${br[3]}` : br[3])
      : now.getFullYear();
    const date = new Date(year, Number(br[2]) - 1, Number(br[1]));
    return date.getMonth() === Number(br[2]) - 1 ? date : null;
  }
  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(value);
  if (iso) {
    return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
  }
  return null;
}

export type SearchResolvers = {
  /** `de:`/`menciona:` → ID da pessoa (nome de usuário, apelido…). */
  user(name: string): string | null;
  /** `em:` → ID do canal (com ou sem `#`). */
  channel(name: string): string | null;
};

export type BuiltSearch = {
  query: SearchQuery;
  /** Filtros que não deu para entender (pessoa ou canal que não existe, data errada). */
  invalid: SearchFilter[];
};

/** Texto da busca → parâmetros da rota de busca do Discord. */
export function buildSearchQuery(
  parsed: ParsedSearch,
  resolvers: SearchResolvers,
  now: Date = new Date(),
): BuiltSearch {
  const query: SearchQuery = {};
  const invalid: SearchFilter[] = [];
  const push = <K extends 'author_id' | 'mentions' | 'channel_id' | 'has'>(
    key: K,
    value: string,
  ) => {
    query[key] = [...(query[key] ?? []), value];
  };
  for (const filter of parsed.filters) {
    switch (filter.key) {
      case 'de':
      case 'menciona': {
        const id = resolvers.user(filter.value.replace(/^@/, ''));
        if (!id) invalid.push(filter);
        else push(filter.key === 'de' ? 'author_id' : 'mentions', id);
        break;
      }
      case 'em': {
        const id = resolvers.channel(filter.value.replace(/^#/, ''));
        if (!id) invalid.push(filter);
        else push('channel_id', id);
        break;
      }
      case 'tem': {
        const has = SEARCH_HAS_VALUES[filter.value.toLowerCase()];
        if (!has) invalid.push(filter);
        else push('has', has);
        break;
      }
      case 'fixada':
        query.pinned = !/^(n[aã]o|false|0)$/i.test(filter.value);
        break;
      case 'antes':
      case 'depois':
      case 'durante': {
        const date = parseSearchDate(filter.value, now);
        // Antes de 2015 não há mensagem (é a época dos IDs do Discord).
        if (!date || date.getFullYear() < 2015) {
          invalid.push(filter);
          break;
        }
        const nextDay = new Date(date);
        nextDay.setDate(date.getDate() + 1);
        if (filter.key === 'antes') query.max_id = snowflakeAt(date);
        if (filter.key === 'depois') query.min_id = snowflakeAt(nextDay);
        if (filter.key === 'durante') {
          query.min_id = snowflakeAt(date);
          query.max_id = snowflakeAt(nextDay);
        }
        break;
      }
    }
  }
  if (parsed.content) query.content = parsed.content;
  return { query, invalid };
}

function normal(texto: string) {
  return texto
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .trim();
}

/** Nome do canal sem o emoji e o separador do começo ("📡┃n2-e-ti" → "n2-e-ti"). */
export function cleanChannelName(nome: string) {
  return nome.replace(/^[^\p{L}\p{N}]+/u, '');
}

/** Quem é `de:fulano` e onde é `em:canal`, pelo que o app já sabe. */
export function searchResolvers(
  estado: ChatState,
  guildId: string | null,
  canalId: string,
): SearchResolvers {
  return {
    user(nome) {
      const busca = normal(nome);
      const ids = guildId
        ? Object.keys(estado.members[guildId] ?? {})
        : [
            ...(estado.channels[canalId]?.recipients ?? []).map(
              (pessoa) => pessoa.id,
            ),
            ...(estado.me ? [estado.me.id] : []),
          ];
      let parecido: string | null = null;
      for (const id of ids) {
        const usuario = estado.users[id];
        if (!usuario) continue;
        const nomes = [
          usuario.username,
          usuario.global_name ?? '',
          selectDisplayName(estado, id, guildId),
        ].map(normal);
        if (nomes.includes(busca)) return id;
        if (!parecido && nomes.some((item) => item.startsWith(busca)))
          parecido = id;
      }
      return parecido;
    },
    channel(nome) {
      if (!guildId) return null;
      const busca = normal(nome);
      const ids = [
        ...(estado.guildChannelIds[guildId] ?? []),
        ...(estado.guildThreadIds[guildId] ?? []),
      ];
      let parecido: string | null = null;
      for (const id of ids) {
        const canal = estado.channels[id];
        if (!canal?.name) continue;
        const nomes = [
          normal(canal.name),
          normal(cleanChannelName(canal.name)),
        ];
        if (nomes.includes(busca)) return id;
        if (!parecido && nomes.some((item) => item.includes(busca)))
          parecido = id;
      }
      return parecido;
    },
  };
}
